import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon, type IconName } from './icons';

export function IconButton({
  icon,
  label,
  active,
  disabled,
  onClick,
  size = 18,
}: {
  icon: IconName;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  size?: number;
}) {
  return (
    <button
      type="button"
      className={`icon-btn ${active ? 'is-active' : ''}`}
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon name={icon} size={size} />
    </button>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: string; icon?: IconName }[];
  onChange: (value: T) => void;
  /** Name of the choice as a whole, e.g. «Кисть», read out before the options. */
  label?: string;
}) {
  // One value out of several is a radio group, not a row of toggles: a screen
  // reader then announces "Перо, 1 из 3, выбрано" rather than three unrelated
  // pressed buttons, and the arrow keys move between them.
  const move = (e: React.KeyboardEvent, from: number) => {
    const dir =
      e.key === 'ArrowRight' || e.key === 'ArrowDown'
        ? 1
        : e.key === 'ArrowLeft' || e.key === 'ArrowUp'
          ? -1
          : 0;
    if (dir === 0) return;
    e.preventDefault();
    const at = (from + dir + options.length) % options.length;
    onChange(options[at]!.value);
    e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('button')[at]?.focus();
  };
  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {options.map((option, i) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            className={active ? 'is-active' : ''}
            title={option.label}
            aria-label={option.label}
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            onKeyDown={(e) => move(e, i)}
            onClick={() => onChange(option.value)}
          >
            {option.icon ? <Icon name={option.icon} size={16} /> : option.label}
          </button>
        );
      })}
    </div>
  );
}

interface Placement {
  top: number;
  left: number;
  maxHeight: number;
  /** Panel sits above the trigger because there was more room there. */
  above: boolean;
}

/** Distance the panel keeps from the viewport edges. */
const EDGE = 8;
/** Distance between the trigger and the panel. */
const OFFSET = 6;

/**
 * Click-outside popover used by every colour and option menu in the panel.
 *
 * The panel itself renders through a portal into `document.body` as
 * `position: fixed`, anchored to the trigger's live coordinates rather than
 * sitting in the document flow under it. A menu positioned relative to its
 * parent gets silently clipped the moment that parent (or any ancestor) is a
 * scroll container — e.g. the organisation list — because a non-`visible`
 * `overflow-y` forces `overflow-x` to clip too. Escaping to the body sidesteps
 * that entirely and keeps every popover, wherever it is mounted, fully
 * visible on screen.
 *
 * Anchoring alone is not enough though: the panel is placed against its own
 * measured size so it can be pulled back inside the viewport, flipped above
 * the trigger, and capped to the space actually available.
 */
export function Popover({
  trigger,
  children,
  align = 'left',
  title,
}: {
  trigger: (open: boolean) => ReactNode;
  children: (close: () => void) => ReactNode;
  align?: 'left' | 'right';
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const reposition = useCallback(() => {
    const anchor = triggerRef.current?.getBoundingClientRect();
    const panel = panelRef.current;
    if (!anchor || !panel) return;

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Measured from the content rather than the box: `scrollWidth`/
    // `scrollHeight` ignore the clamps applied below, so the size a previous
    // pass settled on can never feed back into the next one. What they leave
    // out — borders, and a scrollbar once one appears — is added back.
    const frameX = panel.offsetWidth - panel.clientWidth;
    const frameY = panel.offsetHeight - panel.clientHeight;
    const wantWidth = panel.scrollWidth + frameX;
    const wantHeight = panel.scrollHeight + frameY;

    const width = Math.min(wantWidth, vw - EDGE * 2);
    // Preferred edge first, then dragged back on screen. Without this a
    // right-aligned trigger sitting in the narrow left sidebar hangs its panel
    // off the left side of the window, because the panel is wider than the
    // space between the trigger and that edge.
    const preferred = align === 'right' ? anchor.right - width : anchor.left;
    const left = Math.min(Math.max(preferred, EDGE), Math.max(vw - width - EDGE, EDGE));

    const below = vh - anchor.bottom - OFFSET - EDGE;
    const above = anchor.top - OFFSET - EDGE;
    // Flip up only when that genuinely helps — a cramped panel above a trigger
    // is worse than a scrolling one below it.
    const flip = wantHeight > below && above > below;
    const maxHeight = Math.max(flip ? above : below, Math.min(160, vh - EDGE * 2));
    const height = Math.min(wantHeight, maxHeight);
    const wanted = flip ? anchor.top - OFFSET - height : anchor.bottom + OFFSET;

    const next: Placement = {
      top: Math.min(Math.max(wanted, EDGE), Math.max(vh - height - EDGE, EDGE)),
      left,
      maxHeight,
      above: flip,
    };
    // Returning the previous object when nothing moved keeps the resize
    // observer below from bouncing between two equivalent placements.
    setPlacement((prev) =>
      prev &&
      prev.top === next.top &&
      prev.left === next.left &&
      prev.maxHeight === next.maxHeight &&
      prev.above === next.above
        ? prev
        : next,
    );
  }, [align]);

  // Dropping the placement means the next opening is measured from scratch
  // rather than trusting where the panel sat last time.
  const close = useCallback(() => {
    setOpen(false);
    setPlacement(null);
  }, []);

  // Layout effects run before paint, so the panel — mounted unplaced and
  // hidden purely to be measurable — is already positioned by the first frame
  // the user actually sees.
  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (hostRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // The panel is portalled: closing it with focus inside would drop
      // focus to <body>, so hand it back to the trigger.
      const hadFocus = panelRef.current?.contains(document.activeElement) ?? false;
      close();
      if (hadFocus) triggerRef.current?.focus();
    };
    // Content can arrive after the panel opens — member lists and invites are
    // fetched — and a taller panel may no longer fit where it was put. The
    // inner content box is watched as well as the panel, whose own height
    // stops growing once it hits `maxHeight` and starts scrolling instead.
    const observer = new ResizeObserver(() => reposition());
    if (panelRef.current) {
      observer.observe(panelRef.current);
      if (panelRef.current.firstElementChild) observer.observe(panelRef.current.firstElementChild);
    }
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', reposition);
    // Capture phase so a scroll inside any ancestor (e.g. the org sidebar)
    // is caught too, not just window-level scrolling.
    window.addEventListener('scroll', reposition, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [close, open, reposition]);

  return (
    <div className="popover-host" ref={hostRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`popover-trigger ${open ? 'is-open' : ''}`}
        title={title}
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      >
        {trigger(open)}
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            className={`popover popover-${align} ${placement?.above ? 'is-above' : ''}`}
            role="dialog"
            aria-label={title}
            style={
              placement
                ? { top: placement.top, left: placement.left, maxHeight: placement.maxHeight }
                : { top: 0, left: 0, visibility: 'hidden' }
            }
          >
            {children(close)}
          </div>,
          document.body,
        )}
    </div>
  );
}

export function Swatch({ color, active }: { color: string; active?: boolean }) {
  const transparent = color === 'transparent';
  return (
    <span
      className={`swatch ${active ? 'is-active' : ''} ${transparent ? 'is-transparent' : ''}`}
      style={transparent ? undefined : { background: color }}
    />
  );
}

export function ColorGrid({
  colors,
  value,
  onPick,
}: {
  colors: readonly { name: string; value: string }[];
  value: string;
  onPick: (color: string) => void;
}) {
  return (
    <div className="color-grid">
      {colors.map((color) => (
        <button
          key={color.value}
          type="button"
          title={color.name}
          aria-label={color.name}
          aria-pressed={color.value === value}
          onClick={() => onPick(color.value)}
        >
          <Swatch color={color.value} active={color.value === value} />
        </button>
      ))}
    </div>
  );
}

export function Stepper({
  value,
  min,
  max,
  step = 1,
  suffix,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  onChange: (value: number) => void;
}) {
  return (
    <div className="stepper">
      <button
        type="button"
        aria-label="Уменьшить"
        onClick={() => onChange(Math.max(min, value - step))}
      >
        <Icon name="minus" size={14} />
      </button>
      <input
        type="number"
        value={Math.round(value * 10) / 10}
        min={min}
        max={max}
        step={step}
        onChange={(e) => {
          const next = Number(e.target.value);
          if (Number.isFinite(next)) onChange(Math.min(max, Math.max(min, next)));
        }}
      />
      {suffix && <span className="stepper-suffix">{suffix}</span>}
      <button
        type="button"
        aria-label="Увеличить"
        onClick={() => onChange(Math.min(max, value + step))}
      >
        <Icon name="plus" size={14} />
      </button>
    </div>
  );
}

export function Slider({
  value,
  min,
  max,
  step = 1,
  label,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  step?: number;
  label: string;
  onChange: (value: number) => void;
}) {
  return (
    <label className="slider">
      <span>{label}</span>
      <input
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

export function PanelGroup({ children }: { children: ReactNode }) {
  return <div className="panel-group">{children}</div>;
}

export function PanelDivider() {
  return <div className="panel-divider" />;
}
